/**
 * Filters vehicle price stack based on Hyundai MAAP (Minimum Allowable Advertised Price) rules.
 *
 * When hyundaiMaapPricingEnabled preference is true:
 * - Requires wholesale price to be the first price in the stack
 * - Uses second price as the starting price for MAAP violations
 * - Compares final price to wholesale price (MAAP)
 * - If final price < wholesale (MAAP): Only shows starting price (second price) in price stack
 * - If final price >= wholesale (MAAP): Shows full price stack except wholesale
 * - Always hides wholesale price when preference is enabled
 *
 * Early return conditions (returns inventory unchanged):
 * - If hyundaiMaapPricingEnabled preference is false or not set
 * - If vehicle type is not 'new'
 * - If pricing is missing or invalid
 * - If price stack has less than 3 prices
 * - If first price is not wholesale
 *
 * Prerequisites for MAAP processing:
 * - hyundaiMaapPricingEnabled preference must be 'true'
 * - First price must be wholesale price (typeClass: 'wholesalePrice')
 * - Must have at least 3 prices in stack (wholesale + starting + final)
 * - Must have a valid final price (isFinalPrice: true or fallback to last price)
 *
 * @param {Array} inventory - Array of vehicle objects from WIS response
 * @param {Object} preferences - Preferences object containing configuration values
 * @returns {Array} Modified inventory array with filtered pricing data
 */
export const filterInventoryForMAAP = (inventory, preferences = {}) => {
	// Early return if preference is disabled or inventory is invalid
	if (
		preferences.hyundaiMaapPricingEnabled !== 'true' ||
		!Array.isArray(inventory)
	) {
		return inventory;
	}

	return inventory.map((vehicle) => {
		// Only process new vehicles - return all others unchanged
		if (!vehicle || vehicle.type?.toLowerCase() !== 'new') {
			return vehicle;
		}

		// Extract pricing information - if no pricing exists, return vehicle unchanged
		const { pricing } = vehicle;
		if (!pricing || !Array.isArray(pricing.dprice)) {
			return vehicle;
		}

		const { dprice: priceStack } = pricing;

		// MAAP Prerequisites Check:
		// 1. Must have at least 3 prices (wholesale + starting + final minimum)
		if (priceStack.length < 3) {
			return vehicle;
		}

		// 2. First price must be wholesale price
		const wholesalePrice = priceStack[0];
		if (!wholesalePrice || wholesalePrice.typeClass !== 'wholesalePrice') {
			return vehicle;
		}

		// 3. Second price becomes the starting price for MAAP violations
		const startingPrice = priceStack[1];

		// 4. Find final price using isFinalPrice attribute or fallback to last price
		// Optimization: Search from end to beginning since final price is often last
		let finalPrice = null;
		for (let i = priceStack.length - 1; i >= 0; i--) {
			if (priceStack[i].isFinalPrice === true) {
				finalPrice = priceStack[i];
				break;
			}
		}

		// Fallback: If no price has isFinalPrice, use the last price in the stack
		if (!finalPrice) {
			finalPrice = priceStack[priceStack.length - 1];
		}

		// 5. All required prices must be present
		if (!wholesalePrice || !startingPrice || !finalPrice) {
			return vehicle;
		}

		// Core MAAP logic: Compare final price to wholesale price (MAAP)
		// Parse currency values (e.g., "$21,360" -> 21360)
		// Optimization: Use cached regex for better performance
		const currencyRegex = /[$,]/g;
		const finalPriceValue =
			parseFloat((finalPrice.value || '').replace(currencyRegex, '')) ||
			0;
		const wholesalePriceValue =
			parseFloat(
				(wholesalePrice.value || '').replace(currencyRegex, '')
			) || 0;

		if (finalPriceValue < wholesalePriceValue) {
			// Final price is below MAAP - show only starting price (second price)
			// Optimization: Create minimal object with spread only on necessary properties
			const filteredPriceStack = [
				{
					...startingPrice,
					isFinalPrice: true
				}
			];

			// Create hPrice (hidden price) object with full stack minus wholesale
			// Optimization: slice(1) is efficient as it creates shallow copy
			const hiddenPriceStack = priceStack.slice(1);

			return {
				...vehicle,
				pricing: {
					...pricing,
					dprice: filteredPriceStack,
					hPrice: hiddenPriceStack
				}
			};
		}

		// Final price meets or exceeds MAAP - show full stack except wholesale
		// Optimization: Direct slice operation, no intermediate variable
		return {
			...vehicle,
			pricing: {
				...pricing,
				dprice: priceStack.slice(1) // Skip first price (wholesale)
			}
		};
	});
};

/**
 * Helper function to extract pricing summary for debugging/logging
 * @param {Object} vehicle - Vehicle object
 * @returns {Object} Pricing summary for logging
 */
export const getPricingSummary = (vehicle) => {
	const { pricing = {} } = vehicle || {};
	const { dprice: priceStack = [] } = pricing;

	// Optimization: Single pass through price stack to gather all needed data
	let msrp;
	let finalPrice;
	let wholesale;
	const priceTypes = [];

	priceStack.forEach((price) => {
		priceTypes.push(price.typeClass);

		if (price.typeClass === 'msrp') {
			msrp = price.value;
		} else if (price.typeClass === 'wholesalePrice') {
			wholesale = price.value;
		} else if (price.isFinalPrice === true) {
			finalPrice = price.value;
		}
	});

	return {
		vehicleType: vehicle?.type,
		msrp,
		finalPrice,
		wholesale,
		stackLength: priceStack.length,
		priceTypes
	};
};
